/**
 * MailboxEmailInput + StatusBadge — the per-user delegated mailbox-access UI
 * (Amendment 6).
 *
 * Extracted from InboxEditorModal so other surfaces can import them without
 * pulling in the full five-step editor. Both components are purely
 * presentational and carry no editor-specific state. `StatusBadge` is now also
 * the badge for `MailboxDirectoryPanel`, which is what retired the
 * MicrosoftSetupWizard this comment used to name.
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { providerForMailbox, providerLabel } from '../../../utils/mailboxProvider';
import { connectionStatusFor } from '../../../utils/inboxMailboxes';

export function StatusBadge({ status }) {
    const styles = {
        active: 'bg-emerald-100 text-emerald-800',
        pending: 'bg-amber-100 text-amber-800',
        expired: 'bg-red-100 text-red-800',
        reauth: 'bg-red-100 text-red-800',
        revoked: 'bg-stone-100 text-stone-600',
        none: 'bg-blue-100 text-blue-800',
    };
    const labels = {
        active: 'Approved',
        pending: 'Request Sent',
        expired: 'Expired',
        // `reauth` was missing, and this map has no fallthrough for an unknown
        // key: the Gmail panel passed `status="reauth"` for every mailbox whose
        // token had died, and the badge rendered "Unknown" in the blue
        // "Not Requested" style. The one state that most needs an admin to act
        // was the one the UI could not name.
        reauth: 'Needs Reauth',
        revoked: 'Revoked',
        none: 'Not Requested',
    };
    return (
        <span
            className={`text-xs px-2 py-0.5 rounded-full ${styles[status] || styles.none}`}
            data-testid={`mailbox-status-${status}`}
        >
            {labels[status] || 'Unknown'}
        </span>
    );
}

/**
 * Per-user delegated mailbox input — the per-mailbox flow from Amendment 6.
 *
 * This lets the admin type an email address and request delegated access to it. Each added mailbox shows a status badge (Approved / Request Sent
 * / Expired / Revoked / Not Requested) and action buttons appropriate to that
 * status.
 */
export function MailboxEmailInput({
    label,
    selected,
    onChange,
    tokens,
    accessRequests,
    // Mailbox connections attached to THIS inbox, normalised by
    // `connectionsFromRefs`. Provider-neutral access lives here; `tokens` only
    // ever holds Microsoft delegated grants.
    connections = [],
    onRequestAccess,
    onResend,
    onRevoke,
    onCancel,
    isRequesting,
    isCancelling,
}) {
    const [emailInput, setEmailInput] = useState('');

    const tokenStatus = mail => {
        const list = Array.isArray(tokens) ? tokens : [];
        const token = list.find(t => t.mail === mail);
        // Two stores, one question. A Microsoft delegated grant lands in
        // `mailbox_tokens` (→ `tokens`); a Google one lands in
        // `mailbox_connections` and reaches this component as an attached inbox
        // ref (→ `connections`). Reading only the first is why a Gmail mailbox
        // whose owner had approved the request still rendered "Not Requested"
        // with a Request Access button: no token row, and the control-plane
        // request had already moved off 'sent' to 'approved', so nothing below
        // matched either.
        const connStatus = connectionStatusFor(mail, connections);
        if (token?.status === 'active' || connStatus === 'active') return 'active';
        // An in-flight access request outranks a dead token row. The token row
        // survives revocation/expiry for audit, so after the admin re-requests
        // access the chip must show the consent that is actually pending, not
        // the tombstone — otherwise the row reads "Revoked" forever while an
        // approval email sits in the owner's inbox. Status is matched inside
        // the predicate: the CP keeps closed requests too, and a find on mail
        // alone can land on one of those.
        const reqs = Array.isArray(accessRequests) ? accessRequests : [];
        const req = reqs.find(
            r =>
                r.mailboxEmail === mail &&
                (r.status === 'pending' || r.status === 'sent')
        );
        if (req) return 'pending';
        if (token?.status === 'revoked') return 'revoked';
        if (token?.status === 'expired') return 'expired';
        if (token?.status === 'pending') return 'pending';
        // `needs_reauth` maps onto 'expired' so the row offers Resend — the
        // grant existed and stopped working, which is the same remedy.
        if (connStatus === 'reauth') return 'expired';
        if (connStatus === 'revoked') return 'revoked';
        return 'none';
    };

    const handleAdd = () => {
        const email = emailInput.toLowerCase().trim();
        if (!email || !email.includes('@')) return;
        if (selected.includes(email)) return;
        onChange([...selected, email]);
        setEmailInput('');
    };

    return (
        <div>
            <Label className="text-xs">{label}</Label>
            <div className="flex gap-2 mt-2">
                <Input
                    type="email"
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    onKeyDown={e =>
                        e.key === 'Enter' && (e.preventDefault(), handleAdd())
                    }
                    placeholder="user@company.com"
                    className="flex-1"
                    data-testid="delegated-mailbox-email-input"
                />
                <Button
                    type="button"
                    size="sm"
                    onClick={handleAdd}
                    data-testid="delegated-mailbox-add"
                >
                    Add
                </Button>
            </div>
            {selected.length > 0 && (
                <div className="mt-3 space-y-2">
                    {selected.map(mail => {
                        const status = tokenStatus(mail);
                        return (
                            <div
                                key={mail}
                                className="flex items-center gap-2 p-2 border border-stone-200 rounded-lg"
                                data-testid={`delegated-mailbox-row-${mail}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <span className="block text-sm text-stone-800">
                                        {mail}
                                        {/*
                                          The resolved mail platform, shown per
                                          mailbox. Previously the UI displayed no
                                          platform at all, which is why a
                                          misrouted Workspace mailbox stayed
                                          invisible until the owner landed on the
                                          wrong vendor's login page.
                                        */}
                                        {(() => {
                                            const p = providerForMailbox(mail, {
                                                mailboxTokens: tokens,
                                                accessRequests,
                                            });
                                            if (!p) return null;
                                            return (
                                                <span
                                                    className="ml-2 px-1.5 py-0.5 text-xs rounded bg-stone-100 text-stone-600 align-middle"
                                                    data-testid={`delegated-mailbox-provider-${mail}`}
                                                >
                                                    {providerLabel(p)}
                                                </span>
                                            );
                                        })()}
                                    </span>
                                    {status === 'pending' &&
                                        (() => {
                                            const reqs = Array.isArray(accessRequests)
                                                ? accessRequests
                                                : [];
                                            const req = reqs.find(
                                                r =>
                                                    r.mailboxEmail === mail &&
                                                    (r.status === 'pending' ||
                                                        r.status === 'sent')
                                            );
                                            if (req?.sentAt && req?.expiresAt) {
                                                return (
                                                    <span
                                                        className="block text-xs text-stone-400 mt-0.5"
                                                        data-testid={`delegated-mailbox-meta-${mail}`}
                                                    >
                                                        {`Sent ${formatDistanceToNow(
                                                            new Date(req.sentAt),
                                                            { addSuffix: true }
                                                        )} · Expires in ${formatDistanceToNow(
                                                            new Date(req.expiresAt),
                                                            { addSuffix: false }
                                                        )}`}
                                                    </span>
                                                );
                                            }
                                            return null;
                                        })()}
                                </div>
                                <StatusBadge status={status} />
                                {(status === 'none' || status === 'revoked') && onRequestAccess && (
                                    // Revoked is a valid re-entry point, not a
                                    // terminal state: the token row is only an
                                    // audit tombstone, and a fresh consent
                                    // (FINISH) flips it back to active. Without
                                    // this the mailbox was permanently stuck —
                                    // no button, no path back.
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            onRequestAccess(
                                                mail,
                                                providerForMailbox(mail, {
                                                    mailboxTokens: tokens,
                                                    accessRequests,
                                                })
                                            )
                                        }
                                        disabled={isRequesting}
                                        data-testid={`delegated-request-${mail}`}
                                    >
                                        Request Access
                                    </Button>
                                )}
                                {status === 'expired' && onResend && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => onResend(mail)}
                                        disabled={isRequesting}
                                        data-testid={`delegated-resend-${mail}`}
                                    >
                                        Resend
                                    </Button>
                                )}
                                {status === 'active' && onRevoke && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => onRevoke(mail)}
                                        data-testid={`delegated-revoke-${mail}`}
                                    >
                                        Revoke
                                    </Button>
                                )}
                                {status === 'pending' && onCancel && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => onCancel(mail)}
                                        disabled={isCancelling}
                                        data-testid={`delegated-cancel-${mail}`}
                                    >
                                        {isCancelling ? 'Cancelling…' : 'Cancel'}
                                    </Button>
                                )}
                                {status === 'pending' && onResend && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => onResend(mail)}
                                        disabled={isRequesting}
                                        data-testid={`delegated-resend-pending-${mail}`}
                                    >
                                        Resend
                                    </Button>
                                )}
                                {status === 'active' ? (
                                    // For active tokens, X is disabled — the admin
                                    // should use the Revoke button, which tears
                                    // down subscriptions and marks the token
                                    // revoked. The X button here would only
                                    // remove from the local view, leaving the
                                    // token active server-side.
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        disabled
                                        title="Use the Revoke button to remove an active mailbox"
                                        data-testid={`delegated-remove-disabled-${mail}`}
                                    >
                                        <X className="w-3.5 h-3.5 opacity-40" />
                                    </Button>
                                ) : status === 'pending' && onCancel ? (
                                    // For pending requests, X cancels the
                                    // access request via the control plane.
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        disabled={isCancelling}
                                        onClick={() => {
                                            if (
                                                window.confirm(
                                                    `Remove ${mail}? The pending access request will be cancelled.`
                                                )
                                            ) {
                                                onCancel(mail);
                                            }
                                        }}
                                        data-testid={`delegated-remove-cancel-${mail}`}
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                            onChange(
                                                selected.filter(m => m !== mail)
                                            )
                                        }
                                        data-testid={`delegated-remove-${mail}`}
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </Button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
